import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../api/client';

interface Story {
  id: string;
  title: string;
  slug: string;
  summary: string | null;
  is_active: number;
  createdAt: string;
  modifiedAt: string;
  streamId?: string | null;
  streamTitle?: string | null;
  // These are now computed via joins, not stored
  claimCount?: number;
  materialCount?: number;
}

interface StoriesResponse {
  data: Story[];
  pagination: {
    limit: number;
    offset: number;
    count: number;
  };
}

// Transform API response to expected format
interface TransformedResponse {
  stories: Story[];
  hasMore: boolean;
}

export function useStories(options?: { today?: boolean; date?: string; hours?: number; limit?: number }) {
  const { today = false, date, hours, limit = 50 } = options || {};

  return useQuery({
    queryKey: ['stories', { today, date, hours, limit }],
    queryFn: async (): Promise<TransformedResponse> => {
      const params = new URLSearchParams();
      params.set('limit', limit.toString());
      params.set('sort', 'createdAt');
      params.set('order', 'desc');

      // Date filtering - convert ET dates to UTC for API
      if (date) {
        // Use specific date in ET - convert to UTC range
        const { start, end } = getETDayRangeInUTC(date);
        params.set('createdAt.gte', start);
        params.set('createdAt.lt', end);
      } else if (hours) {
        // Use hours-based filter
        const cutoff = new Date(Date.now() - hours * 60 * 60 * 1000).toISOString();
        params.set('createdAt.gte', cutoff);
      }

      const url = `/stories?${params.toString()}`;
      const response = await apiClient.get<StoriesResponse>(url);

      return {
        stories: response.data || [],
        hasMore: response.pagination?.count === limit,
      };
    },
  });
}

// Convert an ET date (YYYY-MM-DD) to UTC range for API filtering
// E.g., 2026-01-29 ET -> start: 2026-01-29T05:00:00Z, end: 2026-01-30T05:00:00Z
function getETDayRangeInUTC(dateStr: string): { start: string; end: string } {
  // Create date at midnight ET by using the America/New_York timezone
  // We need to find what UTC time corresponds to midnight ET on this date
  const startET = new Date(`${dateStr}T00:00:00`);
  const endET = new Date(`${dateStr}T00:00:00`);
  endET.setDate(endET.getDate() + 1);

  // Get the offset for ET (handles DST automatically)
  const etFormatter = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });

  // Calculate UTC times for midnight ET
  // ET is typically UTC-5 (EST) or UTC-4 (EDT)
  const startUTC = getUTCForETMidnight(dateStr);
  const nextDay = new Date(dateStr + 'T12:00:00');
  nextDay.setDate(nextDay.getDate() + 1);
  const nextDateStr = nextDay.toISOString().split('T')[0];
  const endUTC = getUTCForETMidnight(nextDateStr);

  return { start: startUTC, end: endUTC };
}

// Get UTC ISO string for midnight ET on a given date
function getUTCForETMidnight(dateStr: string): string {
  // Create a date object and find what time in UTC corresponds to midnight ET
  // Use a trick: format a known UTC time in ET, then calculate offset
  const testDate = new Date(`${dateStr}T12:00:00Z`); // noon UTC
  const etString = testDate.toLocaleString('en-US', {
    timeZone: 'America/New_York',
    hour: 'numeric',
    hour12: false
  });
  const etHour = parseInt(etString);
  const offsetHours = 12 - etHour; // How many hours behind UTC is ET

  // Midnight ET = offsetHours:00 UTC
  return `${dateStr}T${offsetHours.toString().padStart(2, '0')}:00:00Z`;
}
